
import React, { createContext, useContext, useState, useEffect } from 'react';
import { RestaurantProfile, restaurantService } from '@/services/api/restaurant';
import { useAuth } from './AuthContext';

interface RestaurantContextType {
  restaurantProfile: RestaurantProfile | null;
  setRestaurantProfile: (profile: RestaurantProfile) => Promise<void>;
  setBusinessUnit: (businessUnitId: number, businessUnitName: string) => Promise<void>;
  isLoading: boolean;
  error: Error | null;
}

const RestaurantContext = createContext<RestaurantContextType | undefined>(undefined);

export function RestaurantProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [restaurantProfile, setRestaurantProfileState] = useState<RestaurantProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    async function loadRestaurantProfile() {
      if (!user?.uid) {
        setIsLoading(false);
        return;
      }

      try {
        const profile = await restaurantService.getRestaurantProfile(user.uid);
        setRestaurantProfileState(profile);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to load restaurant profile'));
      } finally {
        setIsLoading(false);
      }
    }

    loadRestaurantProfile();
  }, [user]);

  const setRestaurantProfile = async (profile: RestaurantProfile) => {
    if (!user?.uid) {
      throw new Error('No user authenticated');
    }

    try {
      await restaurantService.setRestaurantProfile(user.uid, profile);
      setRestaurantProfileState(profile);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to update restaurant profile'));
      throw err;
    }
  };

  const setBusinessUnit = async (businessUnitId: number, businessUnitName: string) => {
    if (!user?.uid) {
      console.error('No user authenticated when trying to set business unit');
      throw new Error('No user authenticated');
    }

    if (!restaurantProfile) {
      console.error('No restaurant profile loaded when trying to set business unit');
      console.log('Creating new restaurant profile with business unit info');
      
      const newProfile = {
        business_unit_id: businessUnitId,
        business_unit_name: businessUnitName
      };

      try {
        await restaurantService.setRestaurantProfile(user.uid, newProfile);
        setRestaurantProfileState(newProfile);
        console.log('Business unit set successfully with new profile');
        return;
      } catch (err) {
        console.error('Failed to create profile with business unit:', err);
        setError(err instanceof Error ? err : new Error('Failed to create restaurant profile'));
        throw err;
      }
    }

    console.log('Setting business unit:', { businessUnitId, businessUnitName, userId: user.uid });
    
    const updatedProfile = {
      ...restaurantProfile,
      business_unit_id: businessUnitId,
      business_unit_name: businessUnitName
    };

    console.log('Updated profile to save:', updatedProfile);

    try {
      await restaurantService.setRestaurantProfile(user.uid, updatedProfile);
      setRestaurantProfileState(updatedProfile);
      console.log('Business unit set successfully');
    } catch (err) {
      console.error('Failed to set business unit:', err);
      setError(err instanceof Error ? err : new Error('Failed to update business unit'));
      throw err;
    }
  };

  return (
    <RestaurantContext.Provider
      value={{
        restaurantProfile,
        setRestaurantProfile,
        setBusinessUnit,
        isLoading,
        error,
      }}
    >
      {children}
    </RestaurantContext.Provider>
  );
}

export function useRestaurant() {
  const context = useContext(RestaurantContext);
  if (context === undefined) {
    throw new Error('useRestaurant must be used within a RestaurantProvider');
  }
  return context;
}
